import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Trophy, Scale, ExternalLink, Zap, Flame, Award, Shield, Wind, Eye, Heart, Compass, Star, MapPin } from 'lucide-react';
import { RARITY_META, RARITY_UI } from '../lib/rarity';
import api from '../lib/api';
import { toast } from 'sonner';
import Confetti from './Confetti';
import RarityBadge from './RarityBadge';

function UserPhoto({ path, fallback, alt }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!path) return;
    let alive = true;
    api.get(`/files/${path}`, { responseType: 'blob' })
      .then(r => { const u = URL.createObjectURL(r.data); if (alive) setUrl(u); })
      .catch(() => {});
    return () => { alive = false; };
  }, [path]);
  return <img src={url || fallback} alt={alt || ''} className="w-full h-full object-cover" />;
}

// Animated integer counter — uses framer's animate() under the hood for smoothness
function Counter({ value, duration = 1.2, delay = 0, className = '', prefix = '', suffix = '', 'data-testid': testId }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf, start;
    const target = Number(value) || 0;
    const to = performance.now() + delay * 1000;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      if (now < to) { raf = requestAnimationFrame(tick); return; }
      if (start === undefined) start = now;
      const p = Math.min(1, (now - start) / (duration * 1000));
      setN(Math.round(target * easeOut(p)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration, delay]);
  return <span className={className} data-testid={testId}>{prefix}{n.toLocaleString()}{suffix}</span>;
}

const REVEAL_START_DELAY = 0.15;
const PER_COMPONENT_DELAY = 0.14;

export default function DiscoveryReveal({ result, onClose }) {
  const [challenging, setChallenging] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [scientific, setScientific] = useState('');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);

  if (!result) return null;

  const rarity = result.species?.rarity || 'common';
  const cardRarity = result.battle_card?.rarity || rarity;
  const ui = RARITY_UI[cardRarity] || RARITY_UI.common;
  const cinematic = ui.cinematic;
  const components = result.score?.components || Object.entries(result.score?.breakdown || {}).map(([label, amount]) => ({ label, amount, explanation: '' }));
  const totalDelay = REVEAL_START_DELAY + (components.length * PER_COMPONENT_DELAY) + 0.25;
  const bc = result.battle_card;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="discovery-reveal"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto"
        style={{ background: cinematic
          ? 'radial-gradient(circle at 50% 20%, rgba(233,196,106,0.18), transparent 55%), rgba(4,10,8,0.97)'
          : 'rgba(4,10,8,0.97)' }}
      >
        {cinematic && <GoldRays />}
        {cinematic && <Confetti seed={result.observation?.observation_id || 'x'} />}

        <div className="max-w-md w-full min-h-screen px-6 pt-16 pb-10 relative">
          <button data-testid="close-reveal" onClick={onClose} className="absolute top-6 right-6 p-2 rounded-full bg-white/5 hover:bg-white/10 z-10"><X className="w-5 h-5" /></button>

          {/* Header */}
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: REVEAL_START_DELAY, type: 'spring', stiffness: 220, damping: 22 }} className="text-center">
            {cinematic && (
              <motion.p initial={{ opacity: 0, letterSpacing: '0.05em' }} animate={{ opacity: 1, letterSpacing: '0.35em' }} transition={{ delay: 0.3, duration: 0.6 }}
                className="text-[10px] uppercase mb-3" style={{ color: ui.color, textShadow: `0 0 12px ${ui.glow || ui.color}` }} data-testid="cinematic-title">
                Incredible Discovery
              </motion.p>
            )}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass mb-4">
              <Sparkles className="w-4 h-4 text-accent" strokeWidth={2} />
              <span className="text-xs uppercase tracking-[0.2em] text-accent">New Discovery</span>
            </div>
            <motion.h2 data-testid="reveal-species-name" initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: REVEAL_START_DELAY + 0.15, duration: 0.6 }}
              className="font-serif text-4xl leading-tight">{result.species.common_name}</motion.h2>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: REVEAL_START_DELAY + 0.35, duration: 0.5 }}
              className="text-sm italic text-muted-foreground mt-1">{result.species.scientific_name}</motion.p>

            <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: REVEAL_START_DELAY + 0.55, type: 'spring', stiffness: 260 }} className="mt-4 flex justify-center">
              <RarityBadge rarity={cardRarity} data-testid="reveal-rarity-badge" />
            </motion.div>
          </motion.div>

          {/* Photos side-by-side */}
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: REVEAL_START_DELAY + 0.7 }} className="relative mt-6 mx-auto">
            <div className="absolute inset-0 blur-3xl opacity-40" style={{ background: `radial-gradient(circle, ${ui.color}, transparent 70%)` }} />
            <div className={`relative rounded-3xl overflow-hidden grid grid-cols-2 gap-px bg-white/10 ${ui.borderClass}`} style={ui.borderStyle}>
              <div className="relative aspect-square bg-black" data-testid="reveal-user-photo">
                <UserPhoto path={result.observation?.image_path} fallback={result.species.image_url || 'https://images.unsplash.com/photo-1552728089-57bdde30beb3?w=800'} alt="Your photo" />
                <div className="absolute inset-x-0 top-0 p-2 bg-gradient-to-b from-black/70 to-transparent">
                  <span className="text-[9px] uppercase tracking-[0.2em] text-white/90">Your Photo</span>
                </div>
              </div>
              <div className="relative aspect-square bg-black" data-testid="reveal-reference-photo">
                {result.species.reference_image_path ? (
                  <UserPhoto path={result.species.reference_image_path} fallback={result.species.image_url || 'https://images.unsplash.com/photo-1552728089-57bdde30beb3?w=800'} alt="Reference" />
                ) : (
                  <img src={result.species.image_url || 'https://images.unsplash.com/photo-1552728089-57bdde30beb3?w=800'} alt="Reference" className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-x-0 top-0 p-2 bg-gradient-to-b from-black/70 to-transparent">
                  <span className="text-[9px] uppercase tracking-[0.2em] text-accent">Reference</span>
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <a data-testid="link-wikipedia"
                 href={`https://en.wikipedia.org/wiki/${encodeURIComponent(result.species.scientific_name || result.species.common_name)}`}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card border border-white/10 text-xs hover:bg-white/5"
              ><ExternalLink className="w-3 h-3" /> Wikipedia</a>
              <a data-testid="link-inaturalist"
                 href={`https://www.inaturalist.org/search?q=${encodeURIComponent(result.species.scientific_name || result.species.common_name)}`}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card border border-white/10 text-xs hover:bg-white/5"
              ><ExternalLink className="w-3 h-3" /> iNaturalist</a>
              {result.area && <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {result.area}</span>}
            </div>
          </motion.div>

          {/* Discovery Score */}
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: REVEAL_START_DELAY + 0.9 }} className="mt-10 text-center">
            <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Discovery Score</p>
            <p className="font-mono font-semibold text-6xl mt-2 tabular-nums" style={{ color: ui.color, textShadow: cinematic ? `0 0 24px ${ui.glow || ui.color}` : undefined }}>
              <Counter value={result.score.total} delay={totalDelay} duration={1.4} data-testid="discovery-score" />
            </p>
          </motion.div>

          {/* Per-component score breakdown — reveal one at a time */}
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: REVEAL_START_DELAY + 1.0 }} className="mt-8 rounded-3xl bg-card border border-white/5 p-5">
            <div className="flex items-center gap-2 mb-4"><Trophy className="w-4 h-4 text-accent" /><h3 className="text-xs uppercase tracking-[0.2em]">Score Breakdown</h3></div>
            <div className="space-y-2.5" data-testid="score-breakdown">
              {components.map((c, i) => (
                <motion.div key={c.label}
                  initial={{ x: -12, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: REVEAL_START_DELAY + 1.1 + i * PER_COMPONENT_DELAY, duration: 0.35 }}
                  className="flex items-start justify-between text-sm gap-3"
                  data-testid="score-row"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate">{c.label}</p>
                    {c.explanation && <p className="text-[10px] text-muted-foreground mt-0.5">{c.explanation}</p>}
                  </div>
                  <span className="font-mono tabular-nums text-accent whitespace-nowrap">
                    +<Counter value={c.amount} delay={REVEAL_START_DELAY + 1.1 + i * PER_COMPONENT_DELAY + 0.05} duration={0.6} />
                  </span>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: totalDelay + 0.2 }}
                className="pt-3 mt-2 border-t border-white/10 flex items-center justify-between">
                <span className="font-serif">Total</span>
                <span className="font-mono font-semibold text-accent">
                  <Counter value={result.score.total} delay={totalDelay + 0.25} duration={0.9} />
                </span>
              </motion.div>
            </div>
          </motion.div>

          {/* Rewards row */}
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: totalDelay + 0.35 }} className="mt-6 grid grid-cols-3 gap-3">
            <Stat icon={Zap} label="XP" value={result.xp_awarded} delay={totalDelay + 0.4} prefix="+" />
            <Stat icon={Flame} label="Streak" value={result.streak} delay={totalDelay + 0.5} suffix="d" />
            <Stat icon={Award} label="Level" value={result.new_level} delay={totalDelay + 0.6} highlight={result.leveled_up} />
          </motion.div>

          {/* Ingredient drops (Plants / Mushrooms / Trees) */}
          {result.ingredient_drops && result.ingredient_drops.length > 0 && (
            <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: totalDelay + 0.75, type: 'spring' }}
              data-testid="ingredient-drops"
              className="mt-6 rounded-3xl bg-gradient-to-br from-emerald-500/15 via-accent/5 to-transparent border border-emerald-400/30 p-5">
              <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-300 mb-2">Ingredient drops</p>
              <div className="flex flex-wrap gap-1.5">
                {result.ingredient_drops.map((d, i) => (
                  <span key={i} data-testid="drop-chip" className="text-xs px-2.5 py-1 rounded-full border border-emerald-400/30 bg-black/30">
                    {d.name} <span className="text-emerald-300">×{d.qty}</span>
                  </span>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">Craft potions at the Alchemy Bench.</p>
            </motion.div>
          )}

          {/* Earned Records */}
          {result.earned_records && result.earned_records.length > 0 && (
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: totalDelay + 0.7, type: 'spring' }}
              data-testid="earned-records"
              className="mt-6 rounded-3xl bg-gradient-to-br from-amber-500/15 via-accent/10 to-transparent border border-accent/40 p-5">
              <div className="flex items-center gap-2 mb-3"><Star className="w-4 h-4 text-accent" /><p className="text-[10px] uppercase tracking-[0.25em] text-accent">Historical First</p></div>
              {result.earned_records.map((r, i) => (
                <div key={r.record_id} className="flex items-center gap-3 py-1.5" data-testid={`record-${r.scope}`}>
                  <span className="text-xl">{recordEmoji(r.scope)}</span>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{formatRecordTitle(r)}</p>
                    <p className="text-[10px] text-muted-foreground">Permanent achievement in your profile</p>
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {/* First-ever 3D unlock */}
          {result.newly_unlocked_3d && (
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: totalDelay + 0.75, type: 'spring' }}
              data-testid="species-3d-unlock"
              className="mt-6 rounded-3xl bg-gradient-to-br from-fuchsia-500/15 via-cyan-500/10 to-transparent border border-fuchsia-400/40 p-5">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-fuchsia-300" />
                <p className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-300">First to the Catalog</p>
              </div>
              <p className="text-sm text-white/95 leading-relaxed">
                You brought <span className="font-serif italic">
                  {result.newly_unlocked_3d.common_name || result.newly_unlocked_3d.scientific_name}
                </span> into the WildQuest 3D catalog. A generation job is now running — every future explorer who catches one will fight with the model you unlocked.
              </p>
              <p className="text-[10px] text-white/50 mt-2">Confirmed via iNaturalist · credited to you forever</p>
            </motion.div>
          )}

          {/* Species Roles hint — non-battle categories */}
          {!bc && (
            <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: totalDelay + 0.85 }}
              data-testid="crafting-only-hint"
              className="mt-6 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3">
              <p className="text-[10px] uppercase tracking-widest text-emerald-200">Crafting resource · No Battle Card</p>
              <p className="text-sm text-white/85 mt-1 leading-relaxed">
                {result.species?.category === 'Plants' && 'Plants are documented respectfully — the pieces you gather feed your alchemy bench, not the arena.'}
                {result.species?.category === 'Mushrooms' && 'Fungi are rare potion ingredients — powerful regeneration and buff components.'}
                {result.species?.category === 'Trees' && 'Trees provide crafting materials: bark, cones, seeds, and habitat clues.'}
                {result.species?.category === 'Minerals' && 'Minerals are catalysts that upgrade and stabilise your crafted items.'}
                {!['Plants','Mushrooms','Trees','Minerals'].includes(result.species?.category) &&
                  'This discovery contributes to your Collections and the crafting bench — not the Battle Arena.'}
              </p>
            </motion.div>
          )}

          {/* Battle Card preview */}
          {bc && (
            <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: totalDelay + 0.85 }}
              data-testid="battle-card-preview"
              className="mt-6 relative rounded-3xl overflow-hidden">
              <div className={`p-4 pb-5 relative ${ui.cardClass}`} style={ui.cardStyle}>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: ui.color }}>Battle Card · {bc.tier}</p>
                  <span className="text-[10px] font-mono text-muted-foreground">{bc.serial_label}</span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-serif text-xl truncate">{bc.species_common_name}</p>
                    <p className="text-[10px] font-mono text-muted-foreground">DNA {bc.dna_seed}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] uppercase tracking-widest text-muted-foreground">Rating</p>
                    <p className="font-mono text-2xl tabular-nums" style={{ color: ui.color }}>
                      <Counter value={bc.battle_rating} delay={totalDelay + 1.0} duration={0.9} />
                    </p>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-4 gap-1.5" data-testid="battle-card-stats">
                  {Object.entries(bc.stats).map(([k, v], i) => (
                    <StatMini key={k} label={k} value={v} icon={STAT_ICONS[k]} delay={totalDelay + 1.05 + i * 0.05} />
                  ))}
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="battle-card-abilities">
                  {bc.abilities.map((a) => (
                    <span key={a} className="text-[10px] px-2 py-0.5 rounded-full border border-white/15 bg-black/20">{a}</span>
                  ))}
                </div>
                {bc.personality?.length > 0 && (
                  <p className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                    Traits: {bc.personality.join(' · ')}
                  </p>
                )}

                <details className="mt-3 group">
                  <summary className="cursor-pointer text-[10px] uppercase tracking-widest text-muted-foreground hover:text-accent">Origin Story</summary>
                  <p className="mt-2 text-xs italic text-foreground/80 leading-relaxed" data-testid="origin-story">{bc.origin_story}</p>
                </details>

                <p className="mt-3 text-[9px] leading-tight text-muted-foreground/80" data-testid="card-disclaimer">
                  {bc.disclaimer}
                </p>
              </div>
            </motion.div>
          )}

          {/* Level up celebration */}
          {result.leveled_up && (
            <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: totalDelay + 1.2, type: 'spring' }}
              data-testid="level-up-card"
              className="mt-6 text-center rounded-3xl bg-gradient-to-br from-accent/20 to-accent/5 border border-accent/40 p-5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-accent">Level Up!</p>
              <p className="font-serif text-3xl mt-2">Level {result.new_level}</p>
            </motion.div>
          )}

          <button data-testid="reveal-continue" onClick={onClose}
            className="mt-8 w-full py-4 rounded-full bg-primary hover:bg-primary-light transition-colors font-medium">
            Continue Exploring
          </button>

          {/* Challenge the AI */}
          {!submitted ? (
            !challenging ? (
              <button data-testid="btn-challenge-ai" onClick={() => setChallenging(true)} className="mt-3 w-full py-3 rounded-full border border-white/10 hover:bg-white/5 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Scale className="w-4 h-4" /> This ID looks wrong — challenge it
              </button>
            ) : (
              <div className="mt-3 rounded-3xl bg-card border border-accent/40 p-5 space-y-3">
                <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Challenge AI Identification</p>
                <p className="text-xs text-muted-foreground">Other explorers will vote. Win: +250 XP · Lose: -100 XP</p>
                <input data-testid="challenge-common" value={suggestion} onChange={e => setSuggestion(e.target.value)} placeholder="Correct common name*" className="w-full bg-black/40 border border-white/10 rounded-full px-4 py-3 text-sm outline-none focus:border-accent" />
                <input data-testid="challenge-sci" value={scientific} onChange={e => setScientific(e.target.value)} placeholder="Scientific name (optional)" className="w-full bg-black/40 border border-white/10 rounded-full px-4 py-3 text-sm italic outline-none focus:border-accent" />
                <textarea data-testid="challenge-note" value={note} onChange={e => setNote(e.target.value)} placeholder="Why? (optional)" className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-sm outline-none focus:border-accent min-h-[64px]" />
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="challenge-cancel" type="button" onClick={() => setChallenging(false)} className="py-3 rounded-full border border-white/10 text-sm text-muted-foreground">Cancel</button>
                  <button
                    data-testid="challenge-submit"
                    type="button"
                    disabled={!suggestion.trim()}
                    onClick={async () => {
                      try {
                        await api.post('/challenges', {
                          observation_id: result.observation.observation_id,
                          suggested_common_name: suggestion.trim(),
                          suggested_scientific_name: scientific.trim(),
                          note: note.trim(),
                        });
                        setSubmitted(true);
                        toast.success('Challenge submitted for community review');
                      } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                    }}
                    className="py-3 rounded-full bg-accent text-black font-medium disabled:opacity-40 text-sm"
                  >Submit</button>
                </div>
              </div>
            )
          ) : (
            <div className="mt-3 rounded-3xl bg-card border border-accent/40 p-5 text-center">
              <Scale className="w-6 h-6 text-accent mx-auto mb-2" />
              <p className="font-serif text-base">Challenge submitted</p>
              <p className="text-xs text-muted-foreground mt-1">The community will vote in the Challenges tab.</p>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

const STAT_ICONS = {
  health: Heart, attack: Zap, defense: Shield, speed: Wind,
  stealth: Eye, awareness: Eye, endurance: Compass, adaptability: Star,
};

function StatMini({ label, value, icon: Icon, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      className="rounded-xl bg-black/30 border border-white/5 px-2 py-1.5 text-center"
    >
      <p className="text-[8px] uppercase tracking-widest text-muted-foreground truncate">{label.slice(0, 3)}</p>
      <p className="font-mono text-sm tabular-nums leading-tight"><Counter value={value} delay={delay} duration={0.5} /></p>
    </motion.div>
  );
}

function Stat({ icon: Icon, label, value, highlight, prefix = '', suffix = '', delay = 0 }) {
  return (
    <div className={`rounded-2xl bg-card border p-3 text-center ${highlight ? 'border-accent/40' : 'border-white/5'}`}>
      {Icon && <Icon className={`w-3.5 h-3.5 mx-auto mb-1 ${highlight ? 'text-accent' : 'text-muted-foreground'}`} strokeWidth={2} />}
      <p className={`font-mono font-semibold text-xl tabular-nums ${highlight ? 'text-accent' : ''}`}>
        <Counter value={value} delay={delay} duration={0.9} prefix={prefix} suffix={suffix} />
      </p>
      <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

function GoldRays() {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 0.6 }} transition={{ duration: 1.2 }}
      className="pointer-events-none absolute inset-x-0 top-0 h-[70vh] overflow-hidden"
    >
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute left-1/2 top-0 h-[80vh] w-[220px] origin-top"
          style={{
            background: `linear-gradient(180deg, rgba(233,196,106,0.20), transparent)`,
            transform: `translateX(-50%) rotate(${(i - 3.5) * 8}deg)`,
            mixBlendMode: 'screen',
          }}
          animate={{ opacity: [0.25, 0.55, 0.25] }}
          transition={{ duration: 3.5 + i * 0.2, repeat: Infinity }}
        />
      ))}
    </motion.div>
  );
}

function recordEmoji(scope) {
  return { world: '🌎', region: '🍁', season: '🌸', month: '📅', park: '🌲' }[scope] || '🏆';
}

function formatRecordTitle(r) {
  if (r.scope === 'world') return `World First — ${r.species_common_name}`;
  if (r.scope === 'region') return `Regional First — ${r.species_common_name}${r.area ? ` in ${r.area}` : ''}`;
  if (r.scope === 'season') return `${(r.scope_value.split('-')[0])} First — ${r.species_common_name}`;
  if (r.scope === 'month') return `Monthly First — ${r.species_common_name}`;
  return `${r.badge} — ${r.species_common_name}`;
}
